import React, {useEffect, useRef} from 'react';

import ProductSearchHitModel from 'coreClasses/search/ProductSearchHitModel';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {IPictureProps} from 'brandComponents/Picture/IPicture';
import ProductTile from 'brandComponents/ProductTile';
import {PRODUCT_IMAGES_SETTINGS} from 'brandConstants';

import OffsetTile from './Subcomponents/OffsetTile';
import ProductsGridCustomElement from './Subcomponents/ProductsGridCustomElement';
import {ISearchHitsProps} from './ISearchHits';

// eslint-disable-next-line no-restricted-imports
import './SearchHits.scss';

const BREAKPOINTS = ViewportMgr.getBreakpoints(true);
// `priority` of the first row (`ProductsGrid.module.scss`): 2 products below `md`, 4 from `md`.
const FIRST_ROW_PRIORITY: IPictureProps['priority'][] = [true, true, BREAKPOINTS.slice(BREAKPOINTS.indexOf('md')), BREAKPOINTS.slice(BREAKPOINTS.indexOf('md'))];

const SearchHits = ({
	classNames,
	searchHits,
	onIntersect = null,
	productHitComponents,
	customProductMediaType,
	productTitleTag = 'h2',
	page,
	pageSize,
	onClick,
	sourceOverwritten,
	lowestPage,
}: ISearchHitsProps) => {
	const firstElement = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (onIntersect && firstElement.current) {
			const options = {
				rootMargin: '0px',
				threshold: 0,
			};

			const observer = new IntersectionObserver(onIntersect, options);

			observer.observe(firstElement.current);

			return () => {
				firstElement.current && observer.unobserve(firstElement.current);
			};
		}

		return () => null;
	}, []);

	if (!searchHits || !searchHits.length) {
		return null;
	}

	return searchHits.map((searchHit: ProductSearchHitModel, index) => {
		return (
			<React.Fragment key={searchHit?.getProductId()}>
				{index % pageSize === 0 && <OffsetTile index={index} page={page} pageSize={pageSize} />}
				<ProductsGridCustomElement index={index} page={page} pageSize={pageSize} />
				<ProductTile
					searchHit={searchHit}
					index={index}
					classNames={classNames}
					productTitleTag={productTitleTag}
					customProductMediaType={customProductMediaType}
					productHitComponents={productHitComponents}
					onClick={onClick}
					ref={index === 0 ? firstElement : null}
					sourceOverwritten={sourceOverwritten}
					showPlaceholder={page > lowestPage}
					priority={PRODUCT_IMAGES_SETTINGS.preloadImages.plp && page === lowestPage && (FIRST_ROW_PRIORITY[index] ?? false)}
				/>
			</React.Fragment>
		);
	});
};

const MemoizedSearchHits = React.memo(SearchHits, (prevProps, nextProps) => {
	const hasSameLength = prevProps.searchHits.length === nextProps.searchHits.length;

	if (!hasSameLength) {
		return false;
	}

	const areSameHits = prevProps.searchHits.every(
		(hit, index) => hit?.getProductId() === nextProps.searchHits[index]?.getProductId()
	);

	return (
		prevProps.sourceOverwritten === nextProps.sourceOverwritten &&
		areSameHits
	);
});

export default MemoizedSearchHits;
